// InspectionsTab.js
// This is the Inspections Tab for the Sampling Event Form

import React, { useRef, useEffect, useMemo } from "react";
import styles from "./InspectionsTab.module.css";

// Components
import { FilterMenu } from "../../common/WellSelectionFilterMenu";
import { WellInspectionCard } from "./components/WellInspectionCard";

// Main InspectionsTab Component
export const InspectionsTab = ({ samplingEventData, tabProps, actions }) => {
  // Getting the actions and ui from the tab props
  const insActions = actions?.samplingEventFormActions?.inspections;
  const ui = tabProps?.inspectionsUI || {};
  const user = tabProps?.user;

  // Checking if the user is the team lead or creator of the sampling event
  const userTeamLeadOrCreator =
    user?.employee_id === samplingEventData?.teamLead ||
    user?.employee_id === samplingEventData?.createdBy ||
    user?.access_level === 2;

  // Getting all the employees from the tab props
  const allEmployees = tabProps?.allEmployees || [];

  const wells = useMemo(() => {
    return samplingEventData?.samplingEventWells || [];
  }, [samplingEventData]);

  // Getting the expanded card id from the ui state
  const expandedCardId = ui.expandedWellId;
  // Function to toggle the expanded card
  const handleExpandWell = (wellId) => insActions.setExpandedWell(wellId);

  // Emit patch function, to be passed to each well card to facilitate the sending of information to the server via websocket.
  const emitPatch = tabProps?.emitPatch;

  // Access nested filter state - ui.filters contains { isFilterMenuOpen, filters: {...}, sort: {...} }
  const filterState = ui.filters || {};
  const activeFilters = filterState.filters || {};
  const activeSort = filterState.sort || { field: "wellId", direction: "asc" };

  // Alphanumeric (natural) sort comparator for well IDs
  // Properly sorts "MW-1", "MW-2", "MW-10" instead of "MW-1", "MW-10", "MW-2"
  const alphanumericCompare = (a, b) => {
    // Split strings into chunks of digits and non-digits
    const chunkify = (str) => {
      const chunks = [];
      let currentChunk = "";
      let isDigit = false;

      for (const char of str) {
        const charIsDigit = /\d/.test(char);
        if (currentChunk && charIsDigit !== isDigit) {
          chunks.push(isDigit ? parseInt(currentChunk, 10) : currentChunk);
          currentChunk = "";
        }
        currentChunk += char;
        isDigit = charIsDigit;
      }
      if (currentChunk) {
        chunks.push(isDigit ? parseInt(currentChunk, 10) : currentChunk);
      }
      return chunks;
    };

    const chunksA = chunkify(String(a).toLowerCase());
    const chunksB = chunkify(String(b).toLowerCase());

    for (let i = 0; i < Math.max(chunksA.length, chunksB.length); i++) {
      const chunkA = chunksA[i];
      const chunkB = chunksB[i];

      // If one is undefined, the shorter one comes first
      if (chunkA === undefined) return -1;
      if (chunkB === undefined) return 1;

      // Compare numbers numerically, strings lexicographically
      if (typeof chunkA === "number" && typeof chunkB === "number") {
        if (chunkA !== chunkB) return chunkA - chunkB;
      } else {
        const strA = String(chunkA);
        const strB = String(chunkB);
        if (strA !== strB) return strA.localeCompare(strB);
      }
    }
    return 0;
  };

  const filteredAndSortedWells = useMemo(() => {
    let result = [...wells];

    if (activeFilters.wellId) {
      const q = String(activeFilters.wellId).toLowerCase();
      result = result.filter((w) => (w.wellId || "").toLowerCase().includes(q));
    }
    if (activeFilters.status) {
      // Exact match for dropdown selection
      result = result.filter((w) => w.status === activeFilters.status);
    }
    if (activeFilters.inspectionStatus === "completed") {
      result = result.filter((w) => w.inspectionCompleted === true);
    } else if (activeFilters.inspectionStatus === "incomplete") {
      result = result.filter((w) => w.inspectionCompleted !== true);
    }

    // sort
    const field = activeSort.field || "wellId";
    const dir = activeSort.direction === "desc" ? -1 : 1;

    result.sort((a, b) => {
      const av = a[field] ?? "";
      const bv = b[field] ?? "";

      // Use alphanumeric sort for wellId, standard localeCompare for other fields
      if (field === "wellId") {
        return alphanumericCompare(av, bv) * dir;
      }

      const as = typeof av === "string" ? av : String(av);
      const bs = typeof bv === "string" ? bv : String(bv);
      return as.localeCompare(bs) * dir;
    });

    return result;
  }, [wells, activeFilters, activeSort]);

  // Use the nested filter state for the FilterMenu component
  const wellFilters = activeFilters;
  const setWellFilters = (filters) => insActions.applyFilters(filters);
  const wellSort = activeSort;
  const setWellSort = (sort) => insActions.setSort(sort);
  const isFilterMenuOpen = !!filterState.isFilterMenuOpen;
  const setIsFilterMenuOpen = (open) => {
    if (open) {
      insActions.openFilterMenu();
    } else {
      insActions.closeFilterMenu();
    }
  };

  // Create refs for filter button and menu
  const filterButtonRef = useRef(null);
  const filterMenuRef = useRef(null);

  // Handle clicks outside filter menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isFilterMenuOpen) return;

      if (
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target) &&
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setIsFilterMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterMenuOpen, setIsFilterMenuOpen]);

  // Toggle filter menu
  const toggleFilterMenu = () => {
    insActions.toggleFilterMenu();
  };

  // Apply filters - called from FilterMenu component
  const applyFilters = (filters, sort) => {
    setWellFilters(filters);
    setWellSort(sort);
  };

  // Reset filters - called from FilterMenu component
  const resetFilters = () => {
    insActions.resetAll();
  };

  // Display message if no wells available in the sampling event
  if (!wells || wells.length === 0) {
    return (
      <div className={styles.inspectionsTabContainer}>
        <div className={styles.noWells}>
          No wells available for inspection. Please make sure wells are assigned
          to this sampling event.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.inspectionsTabContainer}>
      {/* Filter Controls - Always shown even if filteredAndSortedWells is empty */}
      <div className={styles.filterControls}>
        <div className={styles.filterButtonContainer}>
          <button
            className={styles.filterButton}
            onClick={toggleFilterMenu}
            ref={filterButtonRef}
          >
            Filter & Sort Wells
          </button>
          {isFilterMenuOpen && (
            <div ref={filterMenuRef}>
              <FilterMenu
                wellFilters={wellFilters}
                setWellFilters={setWellFilters}
                wellSort={wellSort}
                setWellSort={setWellSort}
                applyFilters={applyFilters}
                resetFilters={resetFilters}
                closeFilterMenu={() => setIsFilterMenuOpen(false)}
                styles={styles}
                filterType="inspections"
                wells={wells}
              />
            </div>
          )}
        </div>
      </div>

      {/* Well Inspection Cards Section */}
      <div className={styles.wellsCardsContainer}>
        {filteredAndSortedWells.length > 0 ? (
          <>
            <p>
              Click on a well card to expand and view/edit inspection details
            </p>

            {filteredAndSortedWells.map((well) => (
              <WellInspectionCard
                key={well.id}
                well={well}
                isExpanded={expandedCardId === well.id}
                onExpandWell={() => handleExpandWell(well.id)}
                user={user}
                allEmployees={allEmployees}
                userAccessLevel={userTeamLeadOrCreator}
                actions={insActions}
                ui={ui}
                emitPatch={emitPatch}
              />
            ))}
          </>
        ) : (
          <div className={styles.noWells}>
            <p>No wells match the current filters.</p>
            <button className={styles.resetButton} onClick={resetFilters}>
              Reset All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
